import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { DrawerCloseEvent, DrawerOpenEvent } from '@theme/theme-drawer';

const PREFIX = 'deepsweep:';
const ENGAGEMENT_SECONDS = [10, 30, 60];
const SCROLL_DEPTHS = [25, 50, 75, 90];
const SOCIAL_HOSTS = ['facebook.com', 'instagram.com', 'pinterest.com', 'tiktok.com', 'youtube.com'];
const onceKeys = new Set();
let cartOpenedAt = 0;
let cartDwellTimer = 0;

function publish(eventName, data = {}) {
  try {
    if (!window.Shopify?.analytics?.publish) return;
    window.Shopify.analytics.publish(`${PREFIX}${eventName}`, {
      event_schema_version: 1,
      source_path: window.location.pathname || '/',
      ...data,
    });
  } catch (_error) {
    // Analytics must never affect storefront behavior.
  }
}

function publishOnce(key, eventName, data = {}) {
  if (onceKeys.has(key)) return;
  onceKeys.add(key);
  publish(eventName, data);
}

function closestElement(target, selector) {
  return target instanceof Element ? target.closest(selector) : null;
}

function safePath(href) {
  try {
    return new URL(href, window.location.href).pathname || '/';
  } catch (_error) {
    return '/';
  }
}

function safeHost(href) {
  try {
    return new URL(href, window.location.href).hostname.toLowerCase().replace(/^www\./, '');
  } catch (_error) {
    return '';
  }
}

function numericData(element, names) {
  for (const name of names) {
    const value = element?.dataset?.[name] || element?.getAttribute?.(`data-${name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`);
    if (value && /^\d+$/.test(String(value))) return String(value);
  }
  return '';
}

function productContext(element) {
  const owner = element?.closest?.('[data-product-id], product-form-component, product-card, [data-ds-product]');
  const variantOwner = element?.closest?.('[data-variant-id], [data-variant]') || owner;
  return {
    product_id: numericData(owner, ['productId']),
    variant_id: numericData(variantOwner, ['variantId', 'variant']),
  };
}

function componentFor(element) {
  if (!element) return 'unknown';
  if (element.closest('header, .header')) return 'header';
  if (element.closest('footer, .footer, [data-ds-footer]')) return 'footer';
  if (element.closest('product-recommendations')) return 'product_recommendations';
  if (element.closest('predictive-search, [data-predictive-search]')) return 'predictive_search';
  if (element.closest('product-card, [data-product-card]')) return 'product_card';
  if (element.closest('#cart-drawer, theme-drawer#cart-drawer')) return 'cart_drawer';
  if (element.closest('[data-ds-video-products], media-gallery')) return 'product_media';
  if (element.closest('[data-ds-fixed-reviews], .jdgm-widget')) return 'reviews';
  if (element.closest('main .shopify-section')) return 'section';
  return 'page';
}

function isCartDrawerEvent(event) {
  const target = event.target instanceof Element ? event.target : null;
  return Boolean(target?.matches?.('theme-drawer#cart-drawer') || target?.closest?.('theme-drawer#cart-drawer'));
}

function cartLineData(event) {
  const line = Array.isArray(event.lines) ? event.lines[0] : null;
  return {
    variant_id: String(line?.merchandiseId || line?.id || ''),
    quantity: Number.isFinite(Number(line?.quantity)) ? Number(line.quantity) : null,
    action: String(event.action || ''),
  };
}

function selectedVariantId(element) {
  const form = element?.closest?.('form[data-type="add-to-cart-form"], product-form-component') ||
    document.querySelector('form[data-type="add-to-cart-form"]');
  return String(form?.querySelector?.('[name="id"]')?.value || productContext(element).variant_id || '');
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const addButton = target.closest(
      '[ref="addToCartButton"], .add-to-cart-button, form[data-type="add-to-cart-form"] button[type="submit"][name="add"]'
    );
    if (addButton) {
      const form = addButton.closest('product-form-component')?.querySelector('form') || addButton.closest('form');
      const variantId = form?.querySelector('[name="id"]')?.value || productContext(addButton).variant_id;
      const quantity = Number(form?.querySelector('[name="quantity"]')?.value || 1);
      publish('atc_button_clicked', {
        ...productContext(addButton),
        variant_id: String(variantId || ''),
        quantity: Number.isFinite(quantity) ? quantity : 1,
        component: componentFor(addButton),
      });
      return;
    }

    const checkout = target.closest('#checkout, [name="checkout"], .cart__checkout-button');
    if (checkout) {
      publish('cart_checkout_clicked', { component: componentFor(checkout) });
      return;
    }

    const sizeGuide = target.closest('[data-size-guide], [href*="size-guide"], [aria-controls*="size-guide"]');
    if (sizeGuide) {
      publish('size_guide_opened', { ...productContext(sizeGuide), component: componentFor(sizeGuide) });
    }

    const reviewControl = target.closest('[data-ds-review-more], [data-ds-review-prev], [data-ds-review-next], .jdgm-widget a, .jdgm-widget button');
    if (reviewControl) {
      publish('review_interacted', {
        ...productContext(reviewControl),
        component: 'reviews',
        action: reviewControl.matches('[data-ds-review-prev]')
          ? 'previous'
          : reviewControl.matches('[data-ds-review-next]')
            ? 'next'
            : reviewControl.matches('[data-ds-review-more]')
              ? 'expand'
              : 'widget_interaction',
      });
    }

    const mediaControl = target.closest('media-gallery button, media-gallery a, [data-ds-video-products] button, [data-ds-video-products] video');
    if (mediaControl) {
      publish('product_media_interacted', {
        ...productContext(mediaControl),
        component: 'product_media',
        action: mediaControl.matches('video') ? 'video' : 'control_clicked',
      });
    }

    const link = target.closest('a[href]');
    if (!link || link.hasAttribute('download') || (link.target === '_blank' && !safeHost(link.href))) return;
    const host = safeHost(link.href);
    const currentHost = window.location.hostname.toLowerCase().replace(/^www\./, '');

    if (host && host !== currentHost) {
      if (SOCIAL_HOSTS.some((socialHost) => host === socialHost || host.endsWith(`.${socialHost}`))) {
        publish('social_outbound_clicked', {
          component: componentFor(link),
          external_host: host,
        });
      }
      return;
    }

    const destinationPath = safePath(link.href);
    const context = {
      ...productContext(link),
      component: componentFor(link),
      destination_path: destinationPath,
      target_type: link.closest('product-card, [data-product-card]') ? 'product' : 'page',
    };

    if (link.closest('product-recommendations')) {
      publish('recommendation_clicked', context);
    } else if (link.closest('#cart-drawer, theme-drawer#cart-drawer') && !destinationPath.startsWith('/checkouts')) {
      publish('cart_continue_shopping_clicked', context);
    } else if (destinationPath !== window.location.pathname || link.hash) {
      publish('internal_navigation_clicked', context);
    }
  },
  true
);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const action = String(event.action || '');
  const data = cartLineData(event);
  event.promise
    ?.then(({ detail }) => {
      if (detail?.didError) {
        if (action === 'add') publish('atc_validation_error', { ...data, error_code: 'cart_add_failed' });
        return;
      }
      if (action === 'remove') publish('cart_item_removed', data);
    })
    .catch(() => {
      if (action === 'add') publish('atc_validation_error', { ...data, error_code: 'cart_add_rejected' });
    });
});

document.addEventListener(StandardEvents.cartError, (event) => {
  publish('atc_validation_error', {
    ...productContext(event.target instanceof Element ? event.target : null),
    error_code: String(event.code || event.detail?.code || 'cart_error').slice(0, 80),
  });
});

document.addEventListener(DrawerOpenEvent.eventName, (event) => {
  if (!isCartDrawerEvent(event)) return;
  cartOpenedAt = Date.now();
  window.clearTimeout(cartDwellTimer);
  publish('cart_drawer_opened', { component: 'cart_drawer' });
  cartDwellTimer = window.setTimeout(() => {
    publish('cart_dwell_10s', { component: 'cart_drawer', seconds: 10 });
  }, 10_000);
});

document.addEventListener(DrawerCloseEvent.eventName, (event) => {
  if (!isCartDrawerEvent(event)) return;
  window.clearTimeout(cartDwellTimer);
  publish('cart_drawer_closed', {
    component: 'cart_drawer',
    seconds: cartOpenedAt ? Math.max(0, Math.round((Date.now() - cartOpenedAt) / 1000)) : null,
  });
  cartOpenedAt = 0;
});

document.addEventListener(StandardEvents.productSelect, (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const selected =
    target?.querySelector?.('[data-variant-id][aria-checked="true"], [data-variant-id]:checked') ||
    target?.closest?.('[data-variant-id]');
  publish('variant_selected', {
    ...productContext(target),
    product_id: String(event.product?.id || productContext(target).product_id || ''),
    variant_id: String(event.detail?.variantId || numericData(selected, ['variantId']) || productContext(target).variant_id || ''),
    option_value: String(event.detail?.optionValueId || ''),
    component: componentFor(target),
  });
});

document.addEventListener('change', (event) => {
  const input = event.target instanceof HTMLInputElement ? event.target : null;
  if (!input) return;

  if (input.matches('input[name="ds-kit"]')) {
    publish('bundle_tier_selected', {
      ...productContext(input),
      variant_id: selectedVariantId(input),
      bundle_tier: String(input.value || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
      component: 'product_form',
    });
    return;
  }

  if (input.matches('input[name="ds-color"]')) {
    publish('variant_selected', {
      ...productContext(input),
      variant_id: selectedVariantId(input),
      option_name: 'color',
      option_value: String(input.value || '').slice(0, 80),
      component: 'product_form',
    });
  }
});

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target?.closest('#cart-drawer, cart-items-component')) return;
  publish('cart_quantity_changed', {
    variant_id: numericData(target, ['variantId']),
    cart_line: Number(event.detail?.cartLine || 0) || null,
    quantity: Number(event.detail?.quantity || 0),
    action: 'requested',
    component: componentFor(target),
  });
});

document.addEventListener(ThemeEvents.mediaStartedPlaying, (event) => {
  const target = event.target instanceof Element ? event.target : null;
  publish('product_media_interacted', {
    ...productContext(target),
    component: 'product_media',
    action: 'media_started_playing',
  });
});

document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target instanceof HTMLDetailsElement ? event.target : null;
    if (!details?.open) return;
    publish('accordion_opened', {
      ...productContext(details),
      component: componentFor(details),
      section_id: details.closest('.shopify-section')?.id || details.id || '',
    });
  },
  true
);

ENGAGEMENT_SECONDS.forEach((seconds) => {
  window.setTimeout(() => {
    if (document.visibilityState === 'visible') {
      publishOnce(`engaged:${seconds}`, 'page_engaged', { seconds });
    }
  }, seconds * 1000);
});

function markScrollDepth(depth) {
  SCROLL_DEPTHS.forEach((threshold) => {
    if (depth >= threshold) publishOnce(`scroll:${threshold}`, 'scroll_depth', { depth: threshold });
  });
}

function trackScrollDepth() {
  const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
  const depth = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
  markScrollDepth(depth);
}

window.addEventListener('scroll', trackScrollDepth, { passive: true });
document.addEventListener('scroll', trackScrollDepth, { passive: true, capture: true });

if (window.location.pathname.startsWith('/products/') && 'IntersectionObserver' in window) {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.3) return;
        const section = entry.target;
        const sectionId = section.id || section.dataset.sectionId || '';
        publishOnce(`section:${sectionId}`, 'pdp_section_viewed', { section_id: sectionId });
        const contentDepth = Math.min(
          100,
          Math.round(((section.offsetTop + section.offsetHeight) / Math.max(1, document.documentElement.scrollHeight)) * 100)
        );
        markScrollDepth(contentDepth);
        observer.unobserve(section);
      });
    },
    { threshold: 0.3 }
  );
  [...document.querySelectorAll('main .shopify-section')]
    .filter((section) => !section.parentElement?.closest('.shopify-section'))
    .forEach((section) => observer.observe(section));
}
